import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';

/**
 * Blog SEO takibi — "yazdığımız içerik ne işe yaradı" sorusunun haftalık cevabı.
 *
 * Sayfanın sırası kasıtlı: önce hafta serisi (yön), sonra hangi yazının
 * kazandığı, sonra hangi sorguda eşikte olduğumuz, en sonda teknik sağlık.
 * Tık sayısı tek başına konulursa yeni bir sitede her hafta "0" yazar ve
 * içeriğin işe yarayıp yaramadığı görünmez; gösterim ve sıra, tıktan önce
 * hareket eden göstergelerdir.
 */

const API_BASE = (() => {
  const raw = (process.env.REACT_APP_API_URL || 'https://api.glowytics.com').trim();
  return raw.replace(/\/+$/, '').replace(/\/api$/, '');
})();
const API = `${API_BASE}/api/blog-seo`;

const nf = (n) => Number(n || 0).toLocaleString('tr-TR');
const pos1 = (v) => (v === null || v === undefined ? '—' : Number(v).toFixed(1));
const ctr = (c, i) => (i > 0 ? `%${((c / i) * 100).toFixed(2)}` : '—');
const shortUrl = (u) => String(u || '').replace(/^https?:\/\/[^/]+/, '') || '/';
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' }) : '—');

const BUCKETS = ['1-10', '11-20', '21-50', '51-100', '100+'];
const BUCKET_COLOR = {
  '1-10': 'bg-emerald-500', '11-20': 'bg-lime-500', '21-50': 'bg-amber-500',
  '51-100': 'bg-orange-500', '100+': 'bg-red-500'
};

function Card({ label, value, sub, tone }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="text-xs text-gray-500">{label}</div>
      <div className={`text-2xl font-semibold mt-1 ${tone || 'text-gray-900'}`}>{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}

/** Kovaların dağılımı tek satırda: ortalama sıranın gizlediği şeyi gösterir. */
function BucketBar({ counts, total }) {
  if (!total) return <span className="text-gray-400 text-sm">—</span>;
  return (
    <div>
      <div className="flex h-3 rounded overflow-hidden">
        {BUCKETS.map(b => counts[b] ? (
          <div key={b} className={BUCKET_COLOR[b]} style={{ width: `${(counts[b] / total) * 100}%` }} title={`${b}. sıra: ${counts[b]}`} />
        ) : null)}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-gray-600">
        {BUCKETS.map(b => counts[b] ? (
          <span key={b} className="flex items-center gap-1">
            <span className={`inline-block w-2 h-2 rounded-sm ${BUCKET_COLOR[b]}`} />
            {b}: <strong>{counts[b]}</strong>
          </span>
        ) : null)}
      </div>
    </div>
  );
}

/** Haftalık seri — SVG, çünkü iki farklı ölçek (gösterim / sıra) yan yana. */
function WeeklyChart({ rows }) {
  if (!rows?.length) return null;
  const W = 720, H = 200, P = { t: 12, r: 44, b: 26, l: 44 };
  const iw = W - P.l - P.r, ih = H - P.t - P.b;
  const maxImp = Math.max(1, ...rows.map(r => r.impressions));
  const maxPos = Math.max(10, ...rows.map(r => r.position || 0));
  const x = (i) => P.l + (rows.length === 1 ? iw / 2 : (i * iw) / (rows.length - 1));
  const yImp = (v) => P.t + ih - (v / maxImp) * ih;
  // Sıra ters çizilir: küçük sayı iyi, yukarıda durmalı.
  const yPos = (v) => P.t + (v / maxPos) * ih;

  const line = (fn, key) => rows.map((r, i) => `${i ? 'L' : 'M'}${x(i)},${fn(r[key] || 0)}`).join(' ');

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxHeight: 220 }}>
      {[0, 0.5, 1].map(t => (
        <line key={t} x1={P.l} x2={W - P.r} y1={P.t + ih * t} y2={P.t + ih * t} stroke="#f1f5f9" />
      ))}
      <path d={line(yImp, 'impressions')} fill="none" stroke="#6366f1" strokeWidth="2" />
      <path d={line(yImp, 'blogImpressions')} fill="none" stroke="#a5b4fc" strokeWidth="2" strokeDasharray="4 3" />
      <path d={line(yPos, 'position')} fill="none" stroke="#f59e0b" strokeWidth="2" />
      {rows.map((r, i) => (
        <g key={r.weekStart}>
          <circle cx={x(i)} cy={yImp(r.impressions)} r="3" fill="#6366f1" />
          <circle cx={x(i)} cy={yPos(r.position || 0)} r="3" fill="#f59e0b" />
          <text x={x(i)} y={H - 8} textAnchor="middle" fontSize="10" fill="#64748b">{fmtDate(r.weekStart)}</text>
        </g>
      ))}
      <text x={P.l - 6} y={P.t + 4} textAnchor="end" fontSize="10" fill="#6366f1">{nf(maxImp)}</text>
      <text x={W - P.r + 6} y={P.t + 4} fontSize="10" fill="#f59e0b">1.</text>
      <text x={W - P.r + 6} y={P.t + ih} fontSize="10" fill="#f59e0b">{Math.round(maxPos)}.</text>
    </svg>
  );
}

function Table({ cols, rows, empty }) {
  if (!rows.length) return <div className="text-sm text-gray-500 py-6 text-center">{empty}</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
            {cols.map(c => <th key={c.key} className={`py-2 px-2 font-medium ${c.right ? 'text-right' : ''}`}>{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
              {cols.map(c => <td key={c.key} className={`py-2 px-2 ${c.right ? 'text-right tabular-nums' : ''}`}>{c.render(r)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function BlogSeo() {
  const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
  const headers = useMemo(
    () => ({ 'Content-Type': 'application/json', ...(brandId ? { 'X-Brand-ID': brandId } : {}) }),
    [brandId]
  );

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [weeks, setWeeks] = useState(12);
  const [tab, setTab] = useState('posts');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/overview?weeks=${weeks}`, { credentials: 'include', headers });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || 'Yüklenemedi');
      setData(j);
    } catch (e) {
      toast.error(e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [headers, weeks]);

  useEffect(() => { load(); }, [load]);

  const runScan = async () => {
    setScanning(true);
    try {
      const res = await fetch(`${API}/health-scan`, { method: 'POST', credentials: 'include', headers, body: '{}' });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || 'Tarama başarısız');
      toast.success(`${j.checked} adres tarandı, ${j.broken} tanesi 200 dönmüyor`);
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setScanning(false);
    }
  };

  if (loading) return <div className="p-6 text-gray-500">Yükleniyor…</div>;
  if (!data) return <div className="p-6 text-gray-500">Veri yok.</div>;
  if (!data.connected) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-semibold">Blog SEO</h1>
        <p className="text-gray-600 mt-2">Bu markada Search Console verisi yok.</p>
      </div>
    );
  }

  const { totals, weekly, buckets, posts, corporate, silent, queries, window: win } = data;
  const last = weekly[weekly.length - 1] || {};
  const prev = weekly[weekly.length - 2] || {};
  const trend = prev.impressions ? ((last.impressions - prev.impressions) / prev.impressions) * 100 : null;

  // Eşikteki sorgular: 11-20. sıra. İlk sayfaya en yakın olanlar, yani bir
  // sonraki işin nereye yapılacağını söyleyen liste.
  const threshold = queries.filter(q => q.bucket === '11-20').sort((a, b) => b.impressions - a.impressions);
  const broken = [...posts, ...corporate].filter(p => p.httpStatus && p.httpStatus !== 200)
    .sort((a, b) => b.impressions - a.impressions);

  const TABS = [
    { key: 'posts', label: `Yazılar (${posts.length})` },
    { key: 'threshold', label: `Eşikteki sorgular (${threshold.length})` },
    { key: 'silent', label: `Sessiz yazılar (${silent.length})` },
    { key: 'health', label: `Teknik sağlık (${broken.length})` }
  ];

  return (
    <div className="p-6 space-y-5 max-w-[1400px]">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Blog SEO Takibi</h1>
          <p className="text-sm text-gray-500 mt-1">
            {data.brand} · Search Console {fmtDate(win.first)} – {fmtDate(win.last)}
            {data.healthCheckedAt && ` · adres taraması ${fmtDate(data.healthCheckedAt)}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={weeks} onChange={e => setWeeks(Number(e.target.value))}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
            {[8, 12, 26, 52].map(w => <option key={w} value={w}>Son {w} hafta</option>)}
          </select>
          <button onClick={runScan} disabled={scanning}
                  className="px-3 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50">
            {scanning ? 'Taranıyor…' : 'Adresleri tara'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card label="Yazılan içerik" value={nf(totals.postsWritten)}
              sub={`${nf(totals.postsEarning)} tanesi gösterim alıyor`} />
        <Card label="Blog gösterimi (28 gün)" value={nf(totals.blog.impressions28)}
              sub={`toplamın %${totals.site.impressions ? Math.round((totals.blog.impressions / totals.site.impressions) * 100) : 0}'i`} />
        <Card label="Blog tıklaması (28 gün)" value={nf(totals.blog.clicks28)}
              sub={`CTR ${ctr(totals.blog.clicks28, totals.blog.impressions28)}`}
              tone={totals.blog.clicks28 ? 'text-gray-900' : 'text-red-600'} />
        <Card label="Kurumsal sayfalar" value={nf(totals.corporate.clicks28)}
              sub={`${nf(totals.corporate.impressions28)} gösterim`} />
        <Card label="Son hafta yönü"
              value={trend === null ? '—' : `${trend > 0 ? '+' : ''}${trend.toFixed(0)}%`}
              sub={`${nf(last.impressions)} gösterim · ort. ${pos1(last.position)}. sıra`}
              tone={trend === null ? '' : trend >= 0 ? 'text-emerald-600' : 'text-red-600'} />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-medium text-gray-900">Haftalık seyir</h2>
          <div className="flex gap-4 text-xs text-gray-600">
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-indigo-500 inline-block" /> tüm site</span>
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-indigo-300 inline-block" /> blog</span>
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-amber-500 inline-block" /> ort. sıra</span>
          </div>
        </div>
        <WeeklyChart rows={weekly} />
        <div className="overflow-x-auto mt-3">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-500 border-b border-gray-200">
                <th className="text-left py-1.5 px-2 font-medium">Hafta</th>
                <th className="text-right py-1.5 px-2 font-medium">Gösterim</th>
                <th className="text-right py-1.5 px-2 font-medium">Blog</th>
                <th className="text-right py-1.5 px-2 font-medium">Tık</th>
                <th className="text-right py-1.5 px-2 font-medium">Ort. sıra</th>
                <th className="text-right py-1.5 px-2 font-medium">Organik oturum</th>
              </tr>
            </thead>
            <tbody>
              {weekly.map(r => (
                <tr key={r.weekStart} className="border-b border-gray-100">
                  <td className="py-1.5 px-2">{fmtDate(r.weekStart)}</td>
                  <td className="py-1.5 px-2 text-right tabular-nums">{nf(r.impressions)}</td>
                  <td className="py-1.5 px-2 text-right tabular-nums text-gray-500">{nf(r.blogImpressions)}</td>
                  <td className="py-1.5 px-2 text-right tabular-nums">{nf(r.clicks)}</td>
                  <td className="py-1.5 px-2 text-right tabular-nums">{pos1(r.position)}</td>
                  <td className="py-1.5 px-2 text-right tabular-nums">{nf(r.organicSessions)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h2 className="font-medium text-gray-900 mb-3">Yazılar hangi sırada</h2>
          <BucketBar counts={buckets.posts} total={posts.length} />
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h2 className="font-medium text-gray-900 mb-3">Sorgular hangi sırada</h2>
          <BucketBar counts={buckets.queries} total={queries.length} />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200">
        <div className="flex gap-1 border-b border-gray-200 px-3 pt-3 overflow-x-auto">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
                    className={`px-3 py-2 text-sm rounded-t-lg whitespace-nowrap ${tab === t.key
                      ? 'bg-gray-100 text-gray-900 font-medium' : 'text-gray-500 hover:text-gray-700'}`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="p-3">
          {tab === 'posts' && (
            <Table
              empty="Gösterim alan yazı yok."
              rows={posts}
              cols={[
                { key: 'title', label: 'Yazı', render: r => (
                  <a href={r.url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">
                    {r.title || shortUrl(r.url)}
                  </a>
                ) },
                { key: 'imp', label: 'Gösterim (28g)', right: true, render: r => nf(r.impressions28) },
                { key: 'clk', label: 'Tık', right: true, render: r => nf(r.clicks28) },
                { key: 'ctr', label: 'CTR', right: true, render: r => ctr(r.clicks28, r.impressions28) },
                { key: 'pos', label: 'Ort. sıra', right: true, render: r => (
                  <span className={r.position <= 10 ? 'text-emerald-600 font-medium' : r.position > 50 ? 'text-red-500' : ''}>
                    {pos1(r.position)}
                  </span>
                ) },
                { key: 'first', label: 'İlk görülme', right: true, render: r => fmtDate(r.firstSeen) },
                { key: 'words', label: 'Kelime', right: true, render: r => (r.words ? nf(r.words) : '—') }
              ]}
            />
          )}

          {tab === 'threshold' && (
            <>
              <p className="text-xs text-gray-500 mb-3 px-2">
                11-20. sıradaki sorgular: ilk sayfaya en yakın olanlar. Bir sonraki güncelleme işi buradan seçilir.
              </p>
              <Table
                empty="Eşikte sorgu yok."
                rows={threshold}
                cols={[
                  { key: 'q', label: 'Sorgu', render: r => r.query },
                  { key: 'imp', label: 'Gösterim', right: true, render: r => nf(r.impressions) },
                  { key: 'clk', label: 'Tık', right: true, render: r => nf(r.clicks) },
                  { key: 'pos', label: 'Sıra', right: true, render: r => pos1(r.position) }
                ]}
              />
            </>
          )}

          {tab === 'silent' && (
            <>
              <p className="text-xs text-gray-500 mb-3 px-2">
                Yayında ama Search Console'da hiç gösterim almamış yazılar. Yeni yazılar için normaldir;
                haftalarca burada kalan bir yazı ya indekslenmemiştir ya da hiçbir sorguya karşılık gelmiyordur.
              </p>
              <Table
                empty="Her yazı gösterim alıyor."
                rows={silent}
                cols={[
                  { key: 'title', label: 'Yazı', render: r => (
                    <a href={r.url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">
                      {r.title || shortUrl(r.url)}
                    </a>
                  ) },
                  { key: 'pub', label: 'Yayın', right: true, render: r => fmtDate(r.publishedAt) },
                  { key: 'words', label: 'Kelime', right: true, render: r => (r.words ? nf(r.words) : '—') },
                  { key: 'http', label: 'HTTP', right: true, render: r => r.httpStatus || '—' }
                ]}
              />
            </>
          )}

          {tab === 'health' && (
            <>
              <p className="text-xs text-gray-500 mb-3 px-2">
                Google'ın hâlâ sıraladığı ama 200 dönmeyen adresler. 301 kendi kendine oturur;
                <strong> 404 ise Google'ın yolladığı trafiği boşa harcıyor</strong> ve elle yönlendirme ister.
              </p>
              <Table
                empty="Tarama yapılmadı ya da tüm adresler 200 dönüyor."
                rows={broken}
                cols={[
                  { key: 'code', label: 'Kod', render: r => (
                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                      r.httpStatus === 404 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                      {r.httpStatus}
                    </span>
                  ) },
                  { key: 'url', label: 'Adres', render: r => (
                    <a href={r.url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">
                      {shortUrl(r.url)}
                    </a>
                  ) },
                  { key: 'to', label: 'Nereye', render: r => (r.redirectTo ? shortUrl(r.redirectTo) : '—') },
                  { key: 'imp', label: 'Gösterim', right: true, render: r => nf(r.impressions) },
                  { key: 'clk', label: 'Tık', right: true, render: r => nf(r.clicks) }
                ]}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
